/**
 * LiveKit Shared Types
 *
 * Types shared between frontend, backend, and Python agent.
 * Used for API requests/responses and call state management.
 */

// =============================================================================
// CALL TYPES
// =============================================================================

/**
 * Voice bot provider
 */
export type VoiceBotProvider = 'pipecat' | 'livekit';

/**
 * Call direction
 */
export type CallDirection = 'inbound' | 'outbound';

/**
 * Call status
 */
export type CallStatus = 'pending' | 'active' | 'ended';

// =============================================================================
// PARTICIPANT TYPES
// =============================================================================

/**
 * LiveKit participant info (used in frontend and backend)
 */
export interface LiveKitParticipant {
  /** Unique participant identity */
  identity: string;
  /** Display name */
  name: string;
  /** Whether this is the local participant */
  isLocal: boolean;
  /** Whether participant is speaking */
  isSpeaking: boolean;
  /** Whether participant's mic is muted */
  isMuted: boolean;
}

// =============================================================================
// CALL METADATA
// =============================================================================

/**
 * LiveKit call metadata (attached to room/participant)
 */
export interface LiveKitCallMetadata {
  /** Unique call ID */
  callId: string;
  /** Business ID */
  businessId: number;
  /** Business name (optional) */
  businessName?: string;
  /** Client WhatsApp number */
  clientWaNumber: string;
  /** Client name (optional) */
  clientName?: string;
  /** Phone number ID for WhatsApp */
  phoneNumberId?: string;
  /** Chatbot ID handling the call */
  chatbotId?: string;
  /** Call direction */
  direction: CallDirection;
  /** Timestamp when call was created */
  createdAt: string;
}

// =============================================================================
// ACTIVE CALL (Redis/State)
// =============================================================================

/**
 * Active call record (stored in Redis, used by backend)
 */
export interface ActiveCall {
  /** WhatsApp call ID */
  callId: string;
  /** Phone number ID */
  phoneNumberId: string;
  /** Voice bot service URL (Pipecat) or empty for LiveKit */
  serviceUrl: string;
  /** WhatsApp access token */
  accessToken: string;
  /** Business ID */
  businessId: number;
  /** Call start timestamp */
  startTime: number;
  /** Voice bot provider */
  provider: VoiceBotProvider;
  /** LiveKit room name (optional) */
  roomName?: string;
  /** LiveKit room SID (optional) */
  roomSid?: string;
  /** Participant SID (optional) */
  participantSid?: string;
  /** Call status */
  status?: CallStatus;
  /** Call source (whatsapp, sip or web) */
  source?: CallSource;
  /** Inbound direct-mode room call (no voice bot): recording starts when an
   *  agent accepts, not at ring time — a ring-only recording would classify
   *  a missed call as answered and bill it. Flipped off once started. */
  recordOnAccept?: boolean;
  /** True when a voice bot is on the call and text messages should be
   *  forwarded via `forwardMsgToVoiceBot`. False for outbound SIP "direct"
   *  mode where an employee handles the call — text chatbot still gets
   *  suppressed, but we skip the pointless `sendDataToRoom` call. */
  hasVoiceBot?: boolean;
}

// =============================================================================
// API PAYLOADS - CREATE ROOM
// =============================================================================

/**
 * Create LiveKit room request payload
 */
export interface CreateLiveKitRoomPayload {
  /** Client WhatsApp number */
  clientWaNumber: string;
  /** Client name (optional) */
  clientName?: string;
  /** Enable call recording */
  enableRecording?: boolean;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Create LiveKit room response
 */
export interface CreateLiveKitRoomResponse {
  /** Room name */
  roomName: string;
  /** Access token for the room */
  token: string;
  /** LiveKit WebSocket URL */
  url: string;
  /** Room SID */
  roomSid: string;
  /** Recording info if enabled */
  recording?: {
    egressId: string;
    status: string;
  } | null;
}

// =============================================================================
// API PAYLOADS - JOIN ROOM
// =============================================================================

/**
 * Join LiveKit room request payload
 */
export interface JoinLiveKitRoomPayload {
  /** Room name to join */
  roomName: string;
  /** Participant type */
  participantType?: 'web' | 'agent';
}

/**
 * Join LiveKit room response
 */
export interface JoinLiveKitRoomResponse {
  /** Access token for the room */
  token: string;
  /** Room name */
  roomName: string;
  /** LiveKit WebSocket URL */
  url: string;
}

// =============================================================================
// API PAYLOADS - END ROOM
// =============================================================================

/**
 * End LiveKit room request payload
 */
export interface EndLiveKitRoomPayload {
  /** Room name to end */
  roomName: string;
}

// =============================================================================
// RECORDING TYPES
// =============================================================================

/**
 * Recording info
 */
export interface LiveKitRecordingInfo {
  /** Egress ID */
  egressId: string;
  /** Recording status */
  status: 'starting' | 'active' | 'ending' | 'complete' | 'failed';
  /** Recording URL (when complete) */
  url?: string;
  /** Recording duration in seconds */
  duration?: number;
}

// =============================================================================
// WEBHOOK TYPES
// =============================================================================

/**
 * LiveKit webhook event types
 */
export type LiveKitWebhookEvent =
  | 'room_started'
  | 'room_finished'
  | 'participant_joined'
  | 'participant_left'
  | 'track_published'
  | 'track_unpublished'
  | 'egress_started'
  | 'egress_updated'
  | 'egress_ended';

/**
 * LiveKit webhook payload
 */
export interface LiveKitWebhookPayload {
  /** Event type */
  event: LiveKitWebhookEvent;
  /** Room info */
  room?: {
    name: string;
    sid: string;
    metadata?: string;
  };
  /** Participant info */
  participant?: {
    identity: string;
    name: string;
    sid: string;
    metadata?: string;
  };
  /** Egress info (for recording events) */
  egressInfo?: {
    egressId: string;
    status: number;
    roomName: string;
  };
  /** Timestamp */
  createdAt?: number;
}

// =============================================================================
// TRANSCRIPT TYPES
// =============================================================================

/**
 * Transcript message
 */
export interface TranscriptMessage {
  /** Role: user or assistant */
  role: 'user' | 'assistant' | 'system';
  /** Message content */
  content: string;
  /** ISO timestamp */
  timestamp: string;
}

/**
 * Tool/function call record
 */
export interface ToolCallRecord {
  /** Function name */
  name: string;
  /** Function arguments */
  arguments: Record<string, unknown>;
  /** Function result */
  result?: string | null;
  /** Whether the result is an error */
  isError?: boolean;
  /** Execution duration in milliseconds */
  durationMs?: number | null;
  /** ISO timestamp */
  timestamp: string;
}

/**
 * Agent session info (sent at call end)
 */
export interface AgentInfo {
  model?: string;
  chatbotId?: string;
  provider?: string;
  voice?: string;
  agentMode?: string;
  participantCount?: number;
  insights?: AgentInsights;
}

export interface AgentInsights {
  userTurns?: number;
  agentTurns?: number;
  interruptions?: number;
  toolCalls?: { total: number; success: number; failed: number };
  responseLatencyMs?: {
    min: number | null;
    max: number | null;
    avg: number | null;
  };
  userSpeechMs?: { min: number | null; max: number | null; avg: number | null };
  agentSpeechMs?: {
    min: number | null;
    max: number | null;
    avg: number | null;
  };
  totalUserSpeechMs?: number;
  totalAgentSpeechMs?: number;
}

// ── Session Report (same structure as LiveKit Cloud dashboard) ──

export interface ChatHistoryMetrics {
  e2e_latency?: number;
  llm_node_ttft?: number;
  tts_node_ttfb?: number;
  transcription_delay?: number;
  end_of_turn_delay?: number;
  /** Time between end-of-turn detection and the agent actually pulling the
   *  user's completed turn into its context (LiveKit internal scheduling). */
  on_user_turn_completed_delay?: number;
  started_speaking_at?: number;
  stopped_speaking_at?: number;
  llm_metadata?: { model_name?: string; model_provider?: string };
  tts_metadata?: { model_name?: string; model_provider?: string };
  stt_metadata?: { model_name?: string; model_provider?: string };
}

export interface ChatHistoryItem {
  id: string;
  type: 'message' | 'function_call' | 'function_call_output' | 'agent_handoff';
  role?: string;
  content?: string[];
  name?: string;
  arguments?: string;
  output?: string;
  call_id?: string;
  is_error?: boolean;
  interrupted?: boolean;
  created_at?: number;
  metrics?: ChatHistoryMetrics;
}

export interface SessionReport {
  job_id?: string;
  room?: string;
  timestamp?: number;
  chat_history?: { items?: ChatHistoryItem[] };
  usage?: Array<{
    model?: string;
    provider?: string;
    type?: string;
    input_tokens?: number;
    output_tokens?: number;
    input_text_tokens?: number;
    input_audio_tokens?: number;
    output_audio_tokens?: number;
  }>;
  options?: {
    max_tool_steps?: number;
    endpointing?: { min_delay?: number; max_delay?: number; mode?: string };
    interruption?: { enabled?: boolean; min_duration?: number };
    preemptive_generation?: { enabled?: boolean };
  };
}

// ── Voice Call (stored in DB, returned by /v1/voice-calls API) ──

/**
 * Unified voice call record — billing + analytics in one shape, matching
 * the `voice_calls` table. One row per `callId`.
 */
export interface VoiceCallData {
  callId: string;
  businessId?: number;
  clientWaNumber?: string;
  chatbotId?: string;

  // Agent config
  model?: string;
  agentMode?: string;

  // Billing (recording-based, source of truth)
  durationSeconds?: number;
  billedMinutes?: number;
  recordingUrl?: string;
  direction?: 'inbound' | 'outbound';

  // Analytics
  participantCount?: number;
  userTurns?: number;
  agentTurns?: number;
  interruptions?: number;
  avgResponseLatencyMs?: number;
  minResponseLatencyMs?: number;
  maxResponseLatencyMs?: number;
  totalUserSpeechMs?: number;
  totalAgentSpeechMs?: number;
  toolCallsTotal?: number;
  toolCallsSuccess?: number;
  toolCallsFailed?: number;

  // Detailed JSON blobs
  toolCallDetails?: ToolCallRecord[];
  sessionReport?: SessionReport;
  rawAgentInfo?: AgentInfo;

  createdAt?: string;
}

/**
 * Save transcript request payload
 */
export interface SaveTranscriptPayload {
  /** Phone number ID */
  phoneNumberId: string;
  /** Call ID */
  callId: string;
  /** Client WhatsApp number */
  clientWaNumber: string;
  /** Transcript messages */
  messages: TranscriptMessage[];
  /** Call duration in seconds */
  duration: number;
  /** Tool calls (optional) */
  toolCalls?: ToolCallRecord[] | null;
  /** Agent session metadata */
  agentInfo?: AgentInfo | null;
  /** Full session report (LiveKit dashboard data) */
  sessionReport?: SessionReport | null;
}

// =============================================================================
// SIP TYPES
// =============================================================================

/**
 * Call source - how the call originated
 */
export type CallSource = 'whatsapp' | 'sip' | 'web';

/** Channel of a LiveKit room call, from its name prefix: `web-{businessId}-…`
 *  → 'web', else 'sip'. Same rule FE/BE. */
export const roomCallSource = (roomName: string): 'sip' | 'web' =>
  roomName.startsWith('web-') ? 'web' : 'sip';

/** The room-name grammar in one place: `sip-{businessId}-…` / `web-{businessId}-…`. */
export const parseRoomName = (
  roomName: string,
): { source: 'sip' | 'web'; businessId: number } | null => {
  const match = roomName.match(/^(sip|web)-(\d+)-/);
  return match
    ? { source: match[1] as 'sip' | 'web', businessId: Number(match[2]) }
    : null;
};

/**
 * SIP call mode
 * - 'agent': AI agent handles the call (default)
 * - 'direct': Employee talks directly via browser
 */
export type SipCallMode = 'agent' | 'direct';

/**
 * SIP provider
 */
export type SipProvider = 'twilio' | 'telnyx' | 'vonage' | 'custom';

/**
 * SIP transport protocol
 * - 'auto': LiveKit auto-detects (tries UDP + TCP, does NOT try TLS)
 * - 'udp': Plain UDP (fast, unencrypted)
 * - 'tcp': Plain TCP (reliable, unencrypted)
 * - 'tls': Encrypted SIP over TLS (required for port 5061 by industry convention)
 */
export type SipTransport = 'auto' | 'udp' | 'tcp' | 'tls';

/**
 * SIP configuration stored per-business
 */
export interface SipConfig {
  /** LiveKit inbound trunk ID */
  inboundTrunkId?: string;
  /** LiveKit outbound trunk ID */
  outboundTrunkId?: string;
  /** LiveKit dispatch rule ID */
  dispatchRuleId?: string;
  /** SIP provider */
  provider?: SipProvider;
  /** SIP server address */
  address?: string;
  /** SIP phone number without country code (e.g. 9876543210). Combine with countryCode to get E.164. */
  phoneNumber?: string;
  /** Country code for the SIP phone number (e.g. 91) */
  countryCode?: number;
  /** Prefix added to outbound destination numbers for provider routing (e.g. '789') */
  dialPrefix?: string;
  /** SIP transport protocol (default: 'auto', use 'tls' for port 5061 servers) */
  transport?: SipTransport;
}

/**
 * Setup SIP request payload
 */
export interface SetupSipRequest {
  /** SIP provider */
  provider: SipProvider;
  /** SIP server address */
  address: string;
  /** Country code (e.g. 91) */
  countryCode: number;
  /** Business WhatsApp number without country code */
  bizWhatsappNumber: string;
  /** SIP auth username */
  authUsername?: string;
  /** SIP auth password */
  authPassword?: string;
  /** Prefix added to outbound destination numbers for provider routing (e.g. '789') */
  dialPrefix?: string;
  /** SIP transport protocol (default: 'auto') */
  transport?: SipTransport;
}

/**
 * Outbound SIP call request payload
 */
export interface OutboundSipCallRequest {
  /** E.164 phone number to call */
  phoneNumber: string;
  /** Chatbot ID to use (optional, defaults to business activeVoiceBotId) */
  chatbotId?: string;
  /** Call mode: 'agent' (AI handles) or 'direct' (employee talks). Defaults to 'agent'. */
  mode?: SipCallMode;
}

/**
 * Response for direct-mode outbound SIP call
 * Frontend uses these to join the LiveKit room directly
 */
export interface OutboundDirectSipCallResponse {
  /** LiveKit room name */
  roomName: string;
  /** Access token for the employee to join the room */
  token: string;
  /** LiveKit WebSocket URL */
  livekitUrl: string;
  /** SIP participant SID */
  participantSid: string;
}

// =============================================================================
// WIDGET CALL SUMMARY
// =============================================================================

/** A call row as the web widget renders it — the outcome only, none of the
 *  inbox-side internals (transcript, recording, event log). */
export interface WidgetCallSummary {
  type: 'call';
  direction: 'USER_INITIATED' | 'BUSINESS_INITIATED';
  /** Seconds on the line; null until answered, or when nobody picked up. */
  duration: number | null;
  /** True once the call has terminated. */
  ended: boolean;
}

/** Call length for the inbox bubble + widget: call event, then recording, then transcript. */
export const callDurationSeconds = (raw: {
  duration?: number | null;
  recordingDuration?: number;
  callEvent?: { duration?: number }[];
}): number | null => {
  const measured = raw.callEvent?.filter(e => e.duration).pop()?.duration;
  return measured || raw.recordingDuration || raw.duration || null;
};

/** The call fields the widget renders: direction, duration and whether it ended. */
export const leanCallInteractive = (raw: {
  direction?: 'USER_INITIATED' | 'BUSINESS_INITIATED';
  duration?: number | null;
  recordingDuration?: number;
  callEvent?: { event?: string; duration?: number }[];
}): WidgetCallSummary => {
  const last = raw.callEvent?.[raw.callEvent.length - 1];
  return {
    type: 'call',
    direction: raw.direction ?? 'USER_INITIATED',
    duration: callDurationSeconds(raw),
    ended: last?.event === 'terminate',
  };
};
